import React from 'react';
import { motion } from 'framer-motion';
import { Clock, CheckCircle, AlertCircle } from 'lucide-react';
import { Card } from './ui/Card';
type ReportStatus = 'submitted' | 'review' | 'assigned' | 'inProgress' | 'resolved';
interface ReportStatusCardProps {
  status: ReportStatus;
  problemType: string;
  roomName: string;
  estimatedTime?: string;
  submittedAt: string;
}
const statusConfig: Record<ReportStatus, {
  label: string;
  icon: React.ReactNode;
  color: string;
  bgColor: string;
  description: string;
}> = {
  submitted: {
    label: 'Inviata',
    icon: <Clock className="w-4 h-4" data-id="element-454" />,
    color: 'text-gray-600',
    bgColor: 'bg-gray-100',
    description: 'La tua segnalazione è stata ricevuta'
  },
  review: {
    label: 'In Revisione',
    icon: <AlertCircle className="w-4 h-4" data-id="element-455" />,
    color: 'text-blue-600',
    bgColor: 'bg-blue-100',
    description: 'Il team sta valutando la segnalazione'
  },
  assigned: {
    label: 'Presa in carico',
    icon: <AlertCircle className="w-4 h-4" data-id="element-456" />,
    color: 'text-teal-600',
    bgColor: 'bg-teal-100',
    description: 'Assegnata a un tecnico'
  },
  inProgress: {
    label: 'In lavorazione',
    icon: <Clock className="w-4 h-4" data-id="element-457" />,
    color: 'text-teal-600',
    bgColor: 'bg-teal-100',
    description: 'Il tecnico sta lavorando al problema'
  },
  resolved: {
    label: 'Risolta',
    icon: <CheckCircle className="w-4 h-4" data-id="element-458" />,
    color: 'text-green-600',
    bgColor: 'bg-green-100',
    description: 'Il problema è stato risolto'
  }
};
export function ReportStatusCard({
  status,
  problemType,
  roomName,
  estimatedTime,
  submittedAt
}: ReportStatusCardProps) {
  const config = statusConfig[status];
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.4
  }} data-id="element-459">
      <Card className="p-4 border border-teal-200 bg-teal-50/50" data-id="element-460">
        <div className="flex items-start gap-3" data-id="element-461">
          {/* Status Icon */}
          <div className={`p-2 flex-shrink-0 ${config.color}`} data-id="element-462">
            {config.icon}
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0" data-id="element-463">
            <div className="flex items-center gap-2 mb-1" data-id="element-464">
              <h3 className="text-base font-bold text-navy" data-id="element-465">
                La tua segnalazione
              </h3>
              <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${config.color} ${config.bgColor}`} data-id="element-466">
                {config.label}
              </span>
            </div>

            <p className="text-gray-600 text-sm mb-2" data-id="element-467">{config.description}</p>

            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500" data-id="element-468">
              <span data-id="element-469">
                <span className="font-medium" data-id="element-470">Problema:</span> {problemType} -{' '}
                {roomName}
              </span>
              <span data-id="element-471">
                <span className="font-medium" data-id="element-472">Inviata:</span> {submittedAt}
              </span>
              {estimatedTime && status !== 'resolved' && <span data-id="element-473">
                  <span className="font-medium" data-id="element-474">Tempo stimato:</span>{' '}
                  {estimatedTime}
                </span>}
            </div>

            {/* Progress Bar - Compact */}
            <div className="mt-3" data-id="element-475">
              <div className="flex items-center" data-id="element-476">
                {['submitted', 'review', 'assigned', 'inProgress', 'resolved'].map((s, index) => {
                const isActive = ['submitted', 'review', 'assigned', 'inProgress', 'resolved'].indexOf(status) >= index;
                const isCurrent = s === status;
                return <div key={s} className="flex-1 flex items-center" data-id="element-477">
                      <div className={`w-2.5 h-2.5 rounded-full transition-all ${isActive ? isCurrent ? 'bg-teal-500 ring-2 ring-offset-1 ring-teal-400' : 'bg-teal-deep' : 'bg-gray-300'}`} data-id="element-478" />
                      {index < 4 && <div className={`flex-1 h-0.5 transition-all ${['submitted', 'review', 'assigned', 'inProgress', 'resolved'].indexOf(status) > index ? 'bg-teal-deep' : 'bg-gray-300'}`} data-id="element-479" />}
                    </div>;
              })}
              </div>
            </div>
          </div>
        </div>
      </Card>
    </motion.div>;
}